<template>
  <div
    ref="imageContainer"
    :class="containerClasses"
    class="a-image"
  >
    <img
      v-if="usePlaceholder"
      :src="placeholderSrc"
      :alt="altText"
      :title="titleText"
      :class="classes"
      class="loaded placeholder"
    >
    <template v-else-if="!usePlaceholder">
      <img
        ref="fullResImage"
        :alt="altText"
        :title="titleText"
        :class="classes"
        class="full-res"
      >
      <img
        ref="lowResImage"
        :src="lowResImageSrc"
        :alt="altText"
        :title="titleText"
        :class="classes"
        class="loaded low-res"
      >
    </template>
  </div>
</template>

<script>
import PlaceHolderImage from './placeholder.svg'

export default {
  name: 'aimage',
  props: {
    img: {
      validator: (val) => typeof val === 'object' || typeof val === 'string' || val === null || val === undefined,
      required: true
    },
    preset: {
      type: [String, Boolean],
      required: false,
      default: false
    },
    title: {
      type: String,
      required: false,
      default: ''
    },
    alt: {
      type: String,
      required: false,
      default: ''
    },
    classes: {
      type: [String, Array],
      required: false,
      default: () => ([])
    },
    responsive: {
      type: Boolean,
      default: false
    },
    contain: {
      type: Boolean,
      default: false
    },
    cover: {
      type: Boolean,
      default: true
    },
    ratio: {
      type: String,
      default: null
    },
    placeholderUrl: {
      type: String,
      default: ''
    },
    hidePlaceholder: {
      type: Boolean,
      default: false
    },
    refresh: {
      type: Boolean,
      default: false
    },
    zoomOnHover: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      refreshRan: false,
      lowResImageSrc: null,
      fullResImageSrc: null,
      usePlaceholder: false,
      observer: null,
      inView: false
    }
  },
  computed: {
    containerClasses() {
      const classes = []
      if (this.responsive) classes.push('responsive')

      if (this.contain) classes.push('contain')
      else if (this.cover) classes.push('cover')

      if (this.ratio) classes.push(`ratio-${this.ratio}`)

      if (this.zoomOnHover) classes.push('zoom')

      return classes
    },
    altText() {
      if (this.alt) {
        return this.alt
      }
      else if (this.img && this.img.data) {
        return this.img.data.alt || ''
      }
      return ''
    },
    titleText() {
      if (this.title) {
        return this.title
      }
      else if (this.img && this.img.data) {
        return this.img.data.title || ''
      }
      return ''
    },
    placeholderSrc() {
      if (!this.hidePlaceholder) {
        return this.placeholderUrl || PlaceHolderImage
      }
      return ''
    },
    containerRef() {
      if (this.$refs) {
        return this.$refs.imageContainer || null
      }
      return null
    },
    fullResImageRef() {
      if (this.$refs) {
        return this.$refs.fullResImage || null
      }
      return null
    },
    lowResImageRef() {
      if (this.$refs) {
        return this.$refs.lowResImage || null
      }
      return null
    }
  },
  watch: {
    img: {
      handler(val) {
        this.loadImage()
      },
      deep: true
    },
    preset(val) {
      this.loadImage()
    },
    inView(val) {
      if (val && this.fullResImageSrc) {
        this.loadFullResImage()
      }
    },
    fullResImageSrc(val) {
      if (val && this.inView) {
        this.loadFullResImage()
      }
    }
  },
  created() {
    this.loadImage()
  },
  mounted() {
    if (window.IntersectionObserver && this.containerRef) {
      this.observer = new IntersectionObserver(this.observerCallback)
      this.observer.observe(this.containerRef)
    }
    else {
      this.inView = true
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  },
  methods: {
    loadImage() {
      if (this.img) {
        let preset = this.preset || this.img.preset || '__original'
        let presetLowRes = preset + '__lowres'
        let imgSpec = {
          ...this.img,
          preset,
          refresh: !this.refreshRan && this.refresh,
          callback: (url) => {
            if (url) {
              this.fullResImageSrc = url
            }
            else {
              this.usePlaceholder = true
            }
          }
        }
        let lowResImgSpec = {
          ...this.img,
          preset: presetLowRes,
          refresh: !this.refreshRan && this.refresh,
          callback: (url) => {
            if (url) {
              this.lowResImageSrc = url
            }
          }
        }
        if (this.img.filepath && this.img.filestorename) {
          if (this.refreshRan || !this.refresh) {
            this.fullResImageSrc = this.$store.getters['filestore/fileCachedImageURL'](imgSpec)
            this.lowResImageSrc = this.$store.getters['filestore/fileCachedImageURL'](lowResImgSpec)
          }
          if (!this.fullResImageSrc) this.$store.dispatch('filestore/getFileURL', imgSpec)
          if (!this.lowResImageSrc) this.$store.dispatch('filestore/getFileURL', lowResImgSpec)
        }
        else {
          this.usePlaceholder = true
        }
      }
      else {
        this.usePlaceholder = true
      }
      this.refreshRan = true
    },
    observerCallback(val) {
      val.some((item) => {
        if (item.isIntersecting) {
          this.inView = true
          if (this.observer) {
            this.observer.disconnect()
            this.observer = null
          }
          return true
        }
      })
    },
    loadFullResImage() {
      if (this.fullResImageSrc && this.fullResImageRef) {
        const fullResImage = new Image()
        const self = this
        fullResImage.onload = function() {
          if (self && self.fullResImageRef && self.lowResImageRef) {
            self.fullResImageRef.src = this.src
            self.fullResImageRef.classList.add('loaded')
            self.lowResImageRef.classList.remove('loaded')
          }
        }
        fullResImage.src = this.fullResImageSrc
      }
    }
  }
}
</script>

<style lang="scss">
  @mixin responsive-ratio($x,$y) {
    $padding: unquote( ( $y / $x ) * 100 + '%' );
    padding-top: $padding;
  }
  .a-image {
    position: relative;
    overflow: hidden;
    z-index: 1;
    display: flex;
    align-items: center;
    width: 100%;
    @include responsive-ratio(16, 9);
    &.cover {
      img {
        object-fit: cover;
        object-position: center;
      }
    }
    &.contain {
      img {
        object-fit: contain;
      }
    }
    img {
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
      width: 100%;
      height: 100%;
      &:not(.loaded) {
        pointer-events: none;
      }
      &.low-res {
        transition-timing-function: ease-in;
        transition: 1.5s;
        z-index: 0;
        &:not(.placeholder) {
          filter: blur(3px);
        }
        &.loaded {
          transition: opacity .25s ease-in, transform .3s ease-in;
        }
      }
      &.full-res {
        transition: opacity .3s ease-in, transform .3s ease-in;
      }
      &.placeholder {
        transition: transform .3s ease-in;
      }
      &.loaded {
        opacity: 1;
      }
      &:not([src]) {
        visibility: hidden;
      }
      &.placeholder {
        object-fit: contain;
      }
    }
    &.ratio-16-10 {
      @include responsive-ratio(16, 10);
    }
    &.ratio-16-9 {
      @include responsive-ratio(16, 9);
    }
    &.ratio-3-2 {
      @include responsive-ratio(3, 2);
    }
    &.ratio-4-3 {
      @include responsive-ratio(4, 3);
    }
    &.ratio-3-4 {
      @include responsive-ratio(3, 4);
    }
    &.ratio-square,
    &.ratio-1-1,
    &.ratio-1-1-round{
      @include responsive-ratio(1, 1);
    }
    &.responsive {
      width: 100%;
      height: 100%;
      padding-top: 0;
    }
    &.zoom {
      img {
        transform: scale(1);
      }
      &:hover {
        img {
          transform: scale(1.1);
        }
      }
    }
  }

</style>
